import { getTheme, IChoiceGroupOption, ITheme, PivotItem } from '@fluentui/react';
import { useBoolean, useId } from '@uifabric/react-hooks';
import { CampusDto } from 'services/client-api-wrapper/campus_api/responses.dto';
import { OrganizationDto, ZammadTicketConfigurationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { IDynamicFormElementProps } from 'components/DynamicForm/DynamicForm';
import IvicosStrings from 'kits/language/stringKit';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { idpService } from 'services/api/identity-provider.service';
import { resourceService } from 'services/api/resource.service';
import { useCampus } from 'shared-state/directory/hooks';
import { useIdentityClaims, useLocalProfile } from 'shared-state/identity/hooks';
import { IProfile } from 'shared-state/identity/types';
import { useCurrentOrgId } from 'shared-state/location/hook';
import { getClassNames } from 'styles/responsiveness';
import defaultLobbyBackgroundImage from './../../media/img/default-lobby.jpg';
import defaultVisitorEntranceImage from './../../media/img/default-bgve.jpg';
import type { IZammadConfiguration } from './Integrations/Zammad/zammad.types';
import type { IOpenProjectConfiguration } from './Integrations/OpenProject/openproject.types';
import type { IDocumensoConfiguration } from './Integrations/Documenso/documenso.types';
import type { IZammadTicketConfiguration } from './Integrations/ZammadTicket/zammadTicket.types';
import type { IColiboConfiguration } from './Integrations/Colibo/colibo.types';

export interface ICampusSettingsState {
    currentUser?: IProfile;
    isFormModalOpen: boolean;
    showFormModal: () => void;
    hideFormModal: () => void;
    titleId: string;
    colorButtonId: string;
    campus?: CampusDto;
    onCampusFormChange: (key: string, newValue: any) => void;
    onUpdateCampus?: (campusId: string, changes: IChanges) => Promise<any | undefined>;
    setChanges: any;
    changes: any;
    formFieldsCampus: IDynamicFormElementProps[];
    isOpen: boolean;
    openPanel: () => void;
    dismissPanel: () => void;
    isDisable: boolean;
    disableButton: () => void;
    notDisableButton: () => void;
    theme: ITheme;
    fetchCampus: () => Promise<void>;
    microsoftProps: { microsoftTenantIsInvited: boolean; isMicrosoftUser: boolean; setMicrosoftTenantIsInvited: (isInvited: boolean) => void };
    pivotDisplaySmallDevice: string;
    selectedKey: string;
    setSelectedKey: React.Dispatch<React.SetStateAction<string>>;
    handleLinkClick: (item?: PivotItem | undefined) => void;
    formFieldsVisitors: IDynamicFormElementProps[];
    onVisitorFormChange: (key: string, newValue: any) => void;
    onUpdateVisitor: (changes: IChanges) => any;
    setRemoveOrgOpened: React.Dispatch<React.SetStateAction<boolean>>;
    removeOrgOpened: boolean;
    performActionRemoveOrg: () => void;
    removeOrgOptions: IChoiceGroupOption[];
    setRemoveOrgChoice: any;
    isSavingChanges: boolean;
    setIsSavingChanges: React.Dispatch<React.SetStateAction<boolean>>;
    dashboardBgImage: number;
    enabledProviders: string[];
    updateProvider: (provider: string, enabled: boolean) => Promise<void>;
    isOrgLoaded: boolean;
    isZammadEnabled: boolean;
    zammadBaseUrl: string;
    updateZammadConfig: (config: IZammadConfiguration) => Promise<OrganizationDto>;
    isOpenProjectEnabled: boolean;
    openProjectBaseUrl: string;
    updateOpenProjectConfig: (config: IOpenProjectConfiguration) => Promise<OrganizationDto>;
    isDocumensoEnabled: boolean;
    documensoBaseUrl: string;
    updateDocumensoConfig: (config: IDocumensoConfiguration) => Promise<OrganizationDto>;
    isZammadTicketEnabled: boolean;
    zammadTicketConfiguration: ZammadTicketConfigurationDto | null;
    updateZammadTicketConfig: (config: IZammadTicketConfiguration) => Promise<OrganizationDto>;
    isColiboEnabled: boolean;
    coliboBaseUrl: string;
    coliboClientId: string;
    updateColiboConfig: (config: IColiboConfiguration) => Promise<OrganizationDto>;
}

export interface IChanges {
    name: string;
    color: string;
    logo: string;
    image: string;
    'video-url': string;
    message: string;
    lobbyBackgroundImage: string;
    dashboard_background_image?: number;
}

export interface IMember {
    userId: string;
    profile_pic: string;
    name: string;
    email: string;
    role: 'user' | 'manager' | 'owner' | 'invited' | 'inactive';
}
export interface IMemberBilling {
    userId: string;
    profile_pic: string;
    name: string;
    email: string;
    role: 'user' | 'manager' | 'owner' | 'invited' | 'inactive';
    sponsor: 'own' | 'other' | 'no-sponsor';
    need_sponsor: boolean;
}

export interface IInvitee {
    email: string;
    role: 'user' | 'manager' | 'inactive';
}

export const useCampusSettingsState = (): any => {
    const routeHistory = useNavigate();
    const currentUser = useLocalProfile();
    const [isFormModalOpen, { setTrue: showFormModal, setFalse: hideFormModal }] = useBoolean(false);
    const titleId = useId('title');
    const colorButtonId = useId('callout-button');

    const [currentOrgId] = useCurrentOrgId();

    const [campus, setCampus] = useCampus();
    const [org, setOrg] = useState<OrganizationDto | undefined>();

    const [, setMembers] = useState<IMember[]>([]);
    const [idClaims] = useIdentityClaims();
    const [invitees, setInvitees] = useState<IInvitee[]>([]);
    const tenantIdEmail = idClaims?.msTenantId + '@microsoft.tenant';
    const isMicrosoftUser = idClaims?.msTenantId ? true : false;
    const microsoftTenantIsInvited = invitees.find((invitee) => invitee.email.includes(tenantIdEmail)) ? true : false;
    const [changes, setChanges] = useState<any>({});
    const [isSavingChanges, setIsSavingChanges] = useState(false);

    const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
    const [isDisable, { setTrue: disableButton, setFalse: notDisableButton }] = useBoolean(true);
    const theme = getTheme();
    const { pivotDisplaySmallDevice } = getClassNames();

    const [selectedKey, setSelectedKey] = React.useState('campus');

    const handleLinkClick = (item?: PivotItem) => {
        item && setSelectedKey(item?.props.itemKey || '');
    };

    const setMicrosoftTenantIsInvited = async (isInvited: boolean) => {
        try {
            if (isInvited) {
                await idpService.newInvited(tenantIdEmail, 'user', 'en');
            } else {
                await idpService.deleteInvited(tenantIdEmail);
            }
        } catch (err) {
            console.log(err);
        }
        fetchUsers();
    };

    const fetchUsers = async () => {
        const fetchedMembers = await idpService.showMembers();
        const fetchedInvitees = await idpService.showOrgInvited();
        setMembers(fetchedMembers);
        setInvitees(fetchedInvitees);
    };

    const fetchCampus = async () => {
        if (!currentOrgId) return;
        const fetchedOrg = await idpService.showOrg();
        setOrg(fetchedOrg);
    };

    useEffect(() => {
        fetchCampus().catch((err: any) => {
            console.error(err);
        });
    }, [currentOrgId]);

    const onChange = (change: { [index: string]: string | boolean }) => {
        setChanges((prevState: any) => {
            const newState = { ...prevState, ...change };
            return newState;
        });
    };

    const onCampusFormChange = (key: string, newValue: any) => {
        onChange({ [key]: newValue }); // Handles changes in the `changes` state
        setFormFieldsCampus((prevArray) => {
            return prevArray.map((el: any) => {
                if (el.key !== key) return el;
                return { ...el, value: newValue }; // Updates the value in formFieldsCampus
            });
        });
    };

    const onVisitorFormChange = (key: string, newValue: any) => {
        onChange({ [key]: newValue });
        setFormFieldsVisitors((prevArray) => {
            return prevArray.map((el: any) => {
                if (el.key != key) return el;
                return { ...el, ...{ value: newValue } };
            });
        });
    };

    const onUpdateCampus = async (campusId: string, changes: IChanges): Promise<any | undefined> => {
        return new Promise((resolve) => {
            if (changes.name == '') return resolve(undefined);

            resourceService
                .updateCampus(campusId, {
                    name: changes.name
                })
                .then((updatedCampus) => {
                    setCampus(updatedCampus);

                    idpService
                        .updateOrg(
                            changes.name,
                            changes.logo,
                            changes.color ? [changes.color] : undefined,
                            changes.lobbyBackgroundImage,
                            changes.image,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            changes.dashboard_background_image
                        )
                        .then((updatedOrg) => {
                            console.log('Updated Org: ***', updatedOrg);
                            setOrg(updatedOrg);
                            resolve(true);
                        });
                });
        });
    };

    const onUpdateVisitor = async (changes: IChanges): Promise<any> => {
        const response = await idpService.updateOrg(
            org?.orgName,
            org?.orgLogo,
            org?.orgColors,
            org?.lobby_background_image,
            changes.image,
            changes['video-url'],
            changes.message
        );
        setOrg(response);
        return response;
    };

    const formFieldsForCampus: (campus: any, org?: OrganizationDto) => IDynamicFormElementProps[] = (campus, org) => {
        return [
            { key: 'name', label: IvicosStrings.campusNameTitle, value: campus?.name || '', type: 'text' },
            {
                key: 'color',
                label: IvicosStrings.campusColorTitle,
                value: org?.orgColors[0] || '',
                type: 'color',
                callout: IvicosStrings.changeColorCalloutTitle
            },
            {
                key: 'logo',
                label: IvicosStrings.campusLogoTitle,
                value: org?.orgLogo || '',
                type: 'image',
                view: 'settings',
                callout: IvicosStrings.changeLogoCalloutTitle,
                buttonText: IvicosStrings.changeLogoActionTitle
            },
            {
                key: 'lobbyBackgroundImage',
                label: IvicosStrings.lobbyImageTitle,
                value: org?.lobby_background_image || '',
                type: 'image',
                view: 'visitor entrance',
                buttonText: IvicosStrings.changeImageActionTitle,
                callout: IvicosStrings.changeLobbyBgImageTitle,
                imageThumbnail: defaultLobbyBackgroundImage
            }
        ];
    };

    const [formFieldsCampus, setFormFieldsCampus] = useState<IDynamicFormElementProps[]>(formFieldsForCampus(campus, org));

    const formFieldsForVisitors: (campus: any, org?: OrganizationDto) => IDynamicFormElementProps[] = (campus, org) => {
        return [
            {
                key: 'image',
                label: IvicosStrings.backgroundImageTitle,
                value: org?.visitor_entrance_background_image || '',
                type: 'image',
                view: 'visitor entrance',
                buttonText: IvicosStrings.changeImageActionTitle,
                callout: IvicosStrings.changeImageCalloutTitle,
                imageThumbnail: defaultVisitorEntranceImage
            },
            {
                key: 'video-url',
                label: IvicosStrings.visitorEntranceYTTitle,
                videoUrl: org?.visitor_entrance_video_url,
                type: 'visitor-entrance-video-player',
                view: 'visitor entrance',
                buttonText: IvicosStrings.changeYTVideo
            },
            {
                key: 'message',
                value: org?.visitor_farewell_message || '',
                type: 'multiline-text',
                maxLength: 272,
                callout: IvicosStrings.farewellMessageCallout
            }
        ];
    };

    const [formFieldsVisitors, setFormFieldsVisitors] = useState<IDynamicFormElementProps[]>(formFieldsForVisitors(campus, org));

    const [removeOrgOpened, setRemoveOrgOpened] = useState<boolean>(false);
    const [removeOrgChoice, setRemoveOrgChoice] = useState<any>('refuse');
    const removeOrgOptions: IChoiceGroupOption[] = [
        { key: 'accept', text: IvicosStrings.removeOrganizationChoiceAccept },
        { key: 'refuse', text: IvicosStrings.removeOrganizationChoiceRefuse }
    ];
    const enabledProviders = org?.dashboard_widget_providers ?? [];

    const updateProvider = async (provider: string, enabled: boolean): Promise<void> => {
        if (!org) return;
        const currentProviders = org?.dashboard_widget_providers ?? [];
        const updatedProviders = enabled ? [...new Set([...currentProviders, provider])] : currentProviders.filter((p) => p !== provider);
        const updatedOrg = await idpService.updateOrg(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            updatedProviders
        );
        setOrg(updatedOrg);
    };

    const isZammadEnabled = org?.zammad_configuration?.is_zammad_enable ?? false;
    const zammadBaseUrl = org?.zammad_configuration?.zammad_base_url ?? '';

    // Unlike updateProvider, this lets a failed request (e.g. a 422 when the backend can't verify
    // the base URL/token against the real Zammad instance) propagate to the caller, so
    // ZammadSettingsForm can catch it and surface the error in the panel instead of it being
    // swallowed here.
    const updateZammadConfig = async (config: IZammadConfiguration): Promise<OrganizationDto> => {
        const updatedOrg = await idpService.updateOrg(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            config
        );
        setOrg(updatedOrg);
        return updatedOrg;
    };

    const isOpenProjectEnabled = org?.openproject_configuration?.is_openproject_enable ?? false;
    const openProjectBaseUrl = org?.openproject_configuration?.openproject_base_url ?? '';

    // Like updateZammadConfig, a failed request (400 for a refused base URL) propagates so the form
    // can show it.
    const updateOpenProjectConfig = async (config: IOpenProjectConfiguration): Promise<OrganizationDto> => {
        const updatedOrg = await idpService.updateOrg(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            config
        );
        setOrg(updatedOrg);
        return updatedOrg;
    };

    const isDocumensoEnabled = org?.documenso_configuration?.is_documenso_enable ?? false;
    const documensoBaseUrl = org?.documenso_configuration?.documenso_base_url ?? '';

    // Like updateOpenProjectConfig, a failed request (400 for a refused base URL) propagates so the form can show it.
    const updateDocumensoConfig = async (config: IDocumensoConfiguration): Promise<OrganizationDto> => {
        const updatedOrg = await idpService.updateOrg(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            config
        );
        setOrg(updatedOrg);
        return updatedOrg;
    };

    const zammadTicketConfiguration = org?.zammad_ticket_configuration ?? null;
    const isZammadTicketEnabled = zammadTicketConfiguration?.is_zammad_ticket_enable ?? false;

    // Like updateZammadConfig, a failed request propagates so the form can show it.
    const updateZammadTicketConfig = async (config: IZammadTicketConfiguration): Promise<OrganizationDto> => {
        const updatedOrg = await idpService.updateOrg(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            config
        );
        setOrg(updatedOrg);
        return updatedOrg;
    };

    const isColiboEnabled = org?.colibo_configuration?.is_colibo_enable ?? false;
    const coliboBaseUrl = org?.colibo_configuration?.colibo_base_url ?? '';
    const coliboClientId = org?.colibo_configuration?.colibo_client_id ?? '';

    // Like updateOpenProjectConfig, a failed request (400 for refused values) propagates so the form can show it.
    const updateColiboConfig = async (config: IColiboConfiguration): Promise<OrganizationDto> => {
        const updatedOrg = await idpService.updateOrg(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            config
        );
        setOrg(updatedOrg);
        return updatedOrg;
    };

    const performActionRemoveOrg = async () => {
        if (removeOrgChoice == 'accept') {
            await idpService.archiveOrg();
            routeHistory('/welcome');
            return;
        }

        setRemoveOrgOpened(false);
    };

    useEffect(() => {
        setFormFieldsCampus(formFieldsForCampus(campus, org));
        setFormFieldsVisitors(formFieldsForVisitors(campus, org));
    }, [campus, org]);

    return {
        currentUser,
        isFormModalOpen,
        showFormModal,
        hideFormModal,
        titleId,
        colorButtonId,
        campus,
        isMicrosoftUser,
        microsoftTenantIsInvited,
        setMicrosoftTenantIsInvited,
        onCampusFormChange,
        onUpdateCampus,
        setChanges,
        changes,
        formFieldsCampus,
        isOpen,
        openPanel,
        dismissPanel,
        isDisable,
        disableButton,
        notDisableButton,
        theme,
        fetchCampus,
        pivotDisplaySmallDevice,
        selectedKey,
        setSelectedKey,
        handleLinkClick,
        formFieldsVisitors,
        onVisitorFormChange,
        onUpdateVisitor,
        setRemoveOrgOpened,
        removeOrgOpened,
        performActionRemoveOrg,
        removeOrgOptions,
        setRemoveOrgChoice,
        isSavingChanges,
        setIsSavingChanges,
        dashboardBgImage: org?.dashboard_background_image ?? 0,
        enabledProviders,
        updateProvider,
        isOrgLoaded: org !== undefined,
        isZammadEnabled,
        zammadBaseUrl,
        updateZammadConfig,
        isOpenProjectEnabled,
        openProjectBaseUrl,
        updateOpenProjectConfig,
        isDocumensoEnabled,
        documensoBaseUrl,
        updateDocumensoConfig,
        isZammadTicketEnabled,
        zammadTicketConfiguration,
        updateZammadTicketConfig,
        isColiboEnabled,
        coliboBaseUrl,
        coliboClientId,
        updateColiboConfig
    };
};
